import { FC, memo, useMemo } from 'react';

import Image from 'next/image';

import { GenExternalLink } from '@/components';

import { isERCBoxSkipRequested } from '@/lib/api/subscriptionManager/helpers/scheduledItemHelpers';
import {
  TypeSubEngineInstallment,
  TypeSubEngineSubscriptionsResponse,
} from '@/lib/api/subscriptionManager/types';

import { useGetProductByPlaygeniusProductAnchor } from '@/lib/contentfulHooks';

import interpolate from '@/utils/translations/interpolate';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';

import { generateZendeskLink } from '@/utils/zendesk';

import { _getFormattedScheduledDate } from '../utils';
import classNames from 'classnames';
import UnpauseERCButton from '../../../../account/UnpauseERCButton';
import { KeyedMutator } from 'swr';

type NextBoxDetailsProps = {
  t: TypeAccountSettingsPageTranslations;
  locale: string | undefined;
  nextUnprocessedItem: TypeSubEngineInstallment;
  isPaused?: boolean;
  refetchSubEngineSubscriptions?: KeyedMutator<TypeSubEngineSubscriptionsResponse>;
};

const PausedBoxResume: FC<NextBoxDetailsProps> = ({
  t,
  locale,
  nextUnprocessedItem,
  refetchSubEngineSubscriptions,
}) => {
  const formattedScheduledDate = _getFormattedScheduledDate({
    scheduled_date: nextUnprocessedItem.scheduled_date,
    locale,
  });

  return (
    <>
      <p className="flex gap-1">
        <span className="font-bold">{t.resumeDateLabel}</span>
        {formattedScheduledDate}
      </p>
      <div className="mt-2" data-testid="unpause-btn-container">
        <UnpauseERCButton
          t={t}
          ercSubscriptionId={nextUnprocessedItem.subscription_id}
          small
          onUnpause={() => refetchSubEngineSubscriptions?.()}
        />
      </div>
    </>
  );
};

const NextBoxShippingAndBilling: FC<NextBoxDetailsProps> = ({
  t,
  locale,
  nextUnprocessedItem,
}) => {
  const formattedScheduledDate = _getFormattedScheduledDate({
    scheduled_date: nextUnprocessedItem.scheduled_date,
    locale,
  });

  return (
    <>
      <p>
        {t.shipDateLabel} {formattedScheduledDate}
      </p>
      <p>
        {t.billDateLabel} {formattedScheduledDate}
      </p>
    </>
  );
};

const NextBoxSkippedMessaging: FC<NextBoxDetailsProps> = ({
  t,
  locale,
  nextUnprocessedItem,
}) => {
  const zendeskLinkUrl = generateZendeskLink(locale);

  return (
    <p data-testid="skippedMessaging">
      {`${t.skippedKitMessagePart1} `}
      <GenExternalLink
        href={zendeskLinkUrl}
        cta={t.contactUs}
        blankTarget
        noFollow
      />{' '}
      {interpolate(t.skippedKitMessagePart2, {
        skipDateCutoff: _getFormattedScheduledDate({
          scheduled_date: nextUnprocessedItem.scheduled_date,
          locale,
        }),
      })}
    </p>
  );
};

const NextBoxDetails: FC<NextBoxDetailsProps> = ({
  t,
  locale,
  nextUnprocessedItem,
  isPaused,
  refetchSubEngineSubscriptions,
}) => {
  const product = useGetProductByPlaygeniusProductAnchor(
    nextUnprocessedItem.product_anchor,
  );

  const isSkipped = isERCBoxSkipRequested(nextUnprocessedItem);

  const RenderedComponent = useMemo(() => {
    if (isPaused) {
      return (
        <PausedBoxResume
          t={t}
          locale={locale}
          nextUnprocessedItem={nextUnprocessedItem}
          refetchSubEngineSubscriptions={refetchSubEngineSubscriptions}
        />
      );
    }

    if (isSkipped) {
      return (
        <NextBoxSkippedMessaging
          t={t}
          locale={locale}
          nextUnprocessedItem={nextUnprocessedItem}
        />
      );
    }

    return (
      <NextBoxShippingAndBilling
        t={t}
        locale={locale}
        nextUnprocessedItem={nextUnprocessedItem}
        isPaused={isPaused}
      />
    );
  }, [
    isPaused,
    isSkipped,
    locale,
    nextUnprocessedItem,
    refetchSubEngineSubscriptions,
    t,
  ]);

  if (!product) {
    return null;
  }

  const { title, shortDescription, featuredImage } = product.fields;

  return (
    <>
      <Image
        src={`https:${featuredImage.fields.Image.fields.file.url}`}
        alt={title}
        width={112}
        height={112}
        className="size-[88px] md:size-28 object-cover bg-blue-light"
      />

      <div
        className={classNames('text-sm', {
          'grid gap-y-0.5': !isPaused,
          'flex flex-col': isPaused,
        })}
        data-testid="nextBoxDetails-description"
      >
        <p className="md:text-lg font-bold">
          {isPaused ? t.currentlyPaused : title}
        </p>

        {!isPaused && <p className="font-bold">{shortDescription}</p>}

        {RenderedComponent}
      </div>
    </>
  );
};

export default memo(NextBoxDetails);
